/**
 * Scraper-plugin access for the Settings modal (§5.1 #35-#38).
 *
 * One shared query key: every mutation answers with the full #35 payload, so the cache is replaced
 * rather than invalidated and the section never flickers through a loading state after a save.
 * Status is invalidated too, because a config save or a successful test can clear a banner.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { ActiveScraperUpdateBody, ScrapersResponse } from '@lw/shared';
import {
  getScrapers,
  setActiveScraper,
  testScraper,
  updateScraperConfig,
} from '../../api/scrapers';
import { QK } from '../../api/queryKeys';

export function useScrapersQuery(enabled = true): UseQueryResult<ScrapersResponse, Error> {
  return useQuery({
    queryKey: QK.scrapers,
    queryFn: ({ signal }) => getScrapers(signal),
    enabled,
    staleTime: 60_000,
  });
}

function useScrapersMutation<TVars>(mutationFn: (vars: TVars) => Promise<ScrapersResponse>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (scrapers) => {
      queryClient.setQueryData(QK.scrapers, scrapers);
      void queryClient.invalidateQueries({ queryKey: QK.status });
    },
  });
}

export function useUpdateScraperConfig() {
  return useScrapersMutation((vars: { pluginId: string; config: Record<string, string | number | boolean> }) =>
    updateScraperConfig(vars.pluginId, vars.config),
  );
}

export function useSetActiveScraper() {
  return useScrapersMutation((vars: ActiveScraperUpdateBody) => setActiveScraper(vars));
}

export function useTestScraper() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pluginId: string) => testScraper(pluginId),
    onSuccess: (result) => {
      // A pass clears that plugin's banner server-side — re-read status so the strip repaints.
      if (result.ok) void queryClient.invalidateQueries({ queryKey: QK.status });
    },
  });
}
